import {
  GREY_300,
  GREY_400,
  GREY_500,
  SPACING_1,
  ScreenReaderOnly,
  TabHeader,
  TabList,
} from '@mnd-frontend/ui';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';

const StyledTabList = styled(TabList)`
  height: 100%;
  border-radius: ${SPACING_1};
  border: none;
  background: ${GREY_300};
  &:hover {
    background: ${GREY_400};
  }
  > button {
    width: 2.75rem;
    height: 2.75rem;
    padding: 0;
    border-radius: ${SPACING_1};
    &:hover {
      background: ${GREY_400};
    }
    &[aria-selected='true'] {
      border: 1px solid ${GREY_500};
    }
  }
`;

export const IconTabs = ({
  tabs,
  activeTab,
  setActiveTab,
}: {
  variant: 'subTabs' | 'mainTabs';
  tabs: {
    icon: ReactNode;
    dataTestId?: string;
  }[];
  fullWidth?: boolean;
} & (
  | {
      activeTab: number;
      setActiveTab: (t: number) => void;
    }
  | {
      activeTab?: undefined;
      setActiveTab?: undefined;
    }
)) => {
  const prefix = useId();
  const [uncontrolledSelectedTab, setUncontrolledSelectedTab] = useState(0);
  const selectedTab = typeof activeTab === 'number' ? activeTab : uncontrolledSelectedTab;
  const setSelectedTab =
    typeof setActiveTab === 'function' ? setActiveTab : setUncontrolledSelectedTab;
  const tabRefs = useRef<Array<HTMLButtonElement>>([]);

  useEffect(() => {
    tabRefs.current = tabRefs.current.slice(0, tabs.length);
  }, [tabs]);

  return (
    <TabHeader>
      <StyledTabList
        $fullWidth={true}
        $variant="subTabs"
        role="tablist"
        onKeyDown={e => {
          if (e.key === 'ArrowLeft') {
            const newSelectedTab = Math.max(selectedTab - 1, 0);
            setSelectedTab(newSelectedTab);
            tabRefs.current[newSelectedTab]?.focus();
          } else if (e.key === 'ArrowRight') {
            const newSelectedTab = Math.min(selectedTab + 1, tabs.length - 1);
            setSelectedTab(newSelectedTab);
            tabRefs.current[newSelectedTab]?.focus();
          }
        }}
      >
        {tabs.map((tab, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            data-testid={tab.dataTestId}
            ref={el => {
              if (el) {
                tabRefs.current[i] = el;
              }
            }}
            tabIndex={selectedTab === i ? 0 : -1}
            id={`${prefix}-tab-${i}`}
            aria-controls={`${prefix}-tabpanel-${i}`}
            aria-selected={selectedTab === i}
            onClick={() => setSelectedTab(i)}
          >
            <ScreenReaderOnly>{`${prefix}-tab-${i}`}</ScreenReaderOnly>
            {tab.icon}
          </button>
        ))}
      </StyledTabList>
    </TabHeader>
  );
};

export default IconTabs;
